import React, { useState, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import {
    Card,
    Row,
    Col,
    Spinner,
    Badge,
    ListGroup,
    Collapse,
} from 'react-bootstrap';

import { useGraphApi } from '@/shared/services/graphApi';

OrganizationNetworkDashboard.propTypes = {
    organizationId: PropTypes.string.isRequired,
};

export default function OrganizationNetworkDashboard({ organizationId }) {
    const [metrics, setMetrics] = useState(null);
    const [neighbors, setNeighbors] = useState(null);
    const [loading, setLoading] = useState(true);
    const [expandedSector, setExpandedSector] = useState(null);

    const graphApi = useGraphApi();

    useEffect(() => {
        const fetchData = async () => {
            try {
                // Fetch both metrics and the actual neighbor data in parallel
                const [metricsData, subgraphData] = await Promise.all([
                    graphApi.fetchOrgMetrics(organizationId),
                    graphApi.fetchSubgraph(organizationId, 1),
                ]);
                setMetrics(metricsData);
                // Filter out the center org from the neighbors list
                const neighborNodes = (subgraphData.nodes || []).filter(
                    (n) => n.id !== organizationId
                );
                setNeighbors(neighborNodes);
            } catch (err) {
                console.error('Failed to load org metrics:', err);
            } finally {
                setLoading(false);
            }
        };
        fetchData();
    }, [graphApi, organizationId]);

    // Group neighbors by sector
    const sectorGroups = useMemo(() => {
        if (!neighbors) return [];
        const groups = new Map();
        for (const n of neighbors) {
            const sector = n.primarySector || 'Unknown';
            if (!groups.has(sector)) {
                groups.set(sector, []);
            }
            groups.get(sector).push(n);
        }
        return Array.from(groups.entries())
            .map(([sector, orgs]) => ({ sector, orgs }))
            .sort((a, b) => b.orgs.length - a.orgs.length);
    }, [neighbors]);

    const handleSectorClick = (sector) => {
        setExpandedSector((prev) => (prev === sector ? null : sector));
    };

    if (loading) {
        return (
            <Card className="border-0 shadow-sm mt-4">
                <Card.Body className="p-4 text-center">
                    <Spinner animation="border" variant="primary" size="sm" />
                </Card.Body>
            </Card>
        );
    }

    if (!metrics) return null;

    return (
        <Card className="border-0 shadow-sm mt-4">
            <Card.Header className="bg-white border-bottom">
                <strong>Network Dashboard</strong>
            </Card.Header>
            <Card.Body>
                <Row className="g-3 mb-3">
                    <Col xs={6}>
                        <Card className="border h-100">
                            <Card.Body className="text-center py-3">
                                <h3 className="text-primary mb-0">
                                    {metrics.directCount}
                                </h3>
                                <small className="text-muted">
                                    Direct Connections
                                </small>
                            </Card.Body>
                        </Card>
                    </Col>
                    <Col xs={6}>
                        <Card className="border h-100">
                            <Card.Body className="text-center py-3">
                                <h3 className="text-primary mb-0">
                                    {metrics.twoHopCount}
                                </h3>
                                <small className="text-muted">
                                    2-Hop Reach
                                </small>
                            </Card.Body>
                        </Card>
                    </Col>
                </Row>

                {sectorGroups.length > 0 && (
                    <>
                        <h6 className="text-muted mb-2">
                            Connections by Sector
                        </h6>
                        <p className="text-muted small mb-2">
                            Click a sector to see connected organizations
                        </p>
                        <ListGroup variant="flush" className="border rounded">
                            {sectorGroups.map((group) => (
                                <React.Fragment key={group.sector}>
                                    <ListGroup.Item
                                        action
                                        onClick={() =>
                                            handleSectorClick(group.sector)
                                        }
                                        className="d-flex justify-content-between align-items-center py-2"
                                    >
                                        <small className="d-flex align-items-center gap-1">
                                            {expandedSector === group.sector
                                                ? '▼'
                                                : '▶'}{' '}
                                            {group.sector}
                                        </small>
                                        <Badge bg="primary" pill>
                                            {group.orgs.length}
                                        </Badge>
                                    </ListGroup.Item>
                                    <Collapse
                                        in={expandedSector === group.sector}
                                    >
                                        <div>
                                            <ListGroup
                                                variant="flush"
                                                className="bg-light"
                                            >
                                                {group.orgs.map((org) => (
                                                    <ListGroup.Item
                                                        key={org.id}
                                                        className="py-1 ps-4 bg-light border-0"
                                                    >
                                                        <Link
                                                            to={`/organizations/${org.id}`}
                                                            className="text-decoration-none small"
                                                        >
                                                            {org.name}
                                                        </Link>
                                                    </ListGroup.Item>
                                                ))}
                                            </ListGroup>
                                        </div>
                                    </Collapse>
                                </React.Fragment>
                            ))}
                        </ListGroup>
                    </>
                )}
            </Card.Body>
        </Card>
    );
}
